import { HTMLAttributes } from 'react'
import { useMedia } from 'react-use'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import {
  craftedToMatterAnimationDesktop,
  craftedToMatterAnimationMobile
} from '@/components/sections/animations/crafted_to_matter'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types/wp'

type CraftedToMatterProps = HTMLAttributes<HTMLDivElement> & {
  title_1?: string
  title_2?: string
  title_3?: string
  image_1?: WPImage
  image_2?: WPImage
  image_3?: WPImage
  text?: string
  link?: WPLink
}

const CraftedToMatter = ({
  title_1,
  title_2,
  title_3,
  image_1,
  image_2,
  image_3,
  text,
  link,
  className,
  ...props
}: CraftedToMatterProps) => {
  const isMobile = useMedia('(max-width: 1024px)', false)

  const renderDesktop = () => (
    <Motion animation={craftedToMatterAnimationDesktop}>
      {(motionRef) => (
        <Section
          className={cn('max-w-full overflow-hidden', className)}
          {...props}
          ref={motionRef}
        >
          <Spacer variant="xl" />
          <Container>
            <Grid data-motion-grid>
              <div className="flex flex-col gap-spacing-xl lg:col-span-5">
                <img
                  src={image_1?.url}
                  alt={image_1?.alt}
                  className="aspect-[4/3] h-auto w-full object-cover object-center"
                  data-motion-image-1
                />
                <div className="flex">
                  <Typography variant="h1" component="span" data-motion-title-1>
                    {title_1}
                  </Typography>
                </div>
              </div>
              <div className="flex flex-col gap-spacing-xl lg:col-span-2">
                <img
                  src={image_2?.url}
                  alt={image_2?.alt}
                  className="aspect-[3/4] h-auto w-full object-cover object-center"
                  data-motion-image-2
                />
                <div className="flex">
                  <Typography variant="h1" component="span" data-motion-title-2>
                    {title_2}
                  </Typography>
                </div>
              </div>
              <div className="flex flex-col gap-spacing-xl lg:col-span-5">
                <div className="flex">
                  <Typography variant="h1" component="span" data-motion-title-3>
                    {title_3}
                  </Typography>
                </div>
                <img
                  src={image_3?.url}
                  alt={image_3?.alt}
                  className="aspect-[4/3] h-auto w-full object-cover object-center"
                  data-motion-image-3
                />
              </div>
            </Grid>
            <Grid className="mt-spacing-xl">
              <div className="flex flex-col gap-spacing-md lg:col-span-5 lg:col-start-7">
                <div className="flex" data-motion-text>
                  <Typography variant="h6" component="p">
                    {text}
                  </Typography>
                </div>
                {link?.url ? (
                  <CtaButton
                    href={link.url}
                    variant="secondary"
                    className="self-start"
                    data-motion-cta
                  >
                    {link?.title || 'Scopri di più'}
                  </CtaButton>
                ) : null}
              </div>
            </Grid>
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )

  const renderMobile = () => (
    <Motion animation={craftedToMatterAnimationMobile}>
      {(motionRef) => (
        <Section className={cn(className)} {...props} ref={motionRef}>
          <Spacer variant="xl" />
          <Container>
            <Grid data-motion-grid className="gap-y-spacing-xl">
              <div className="col-span-2 md:col-span-6">
                <ParallaxImage className="aspect-[4/3]">
                  <img
                    src={image_1?.url}
                    alt={image_1?.alt}
                    className="aspect-[4/3] h-auto w-full object-cover object-center"
                    data-motion-image-1
                  />
                </ParallaxImage>
              </div>

              <div className="col-span-2 flex flex-col gap-spacing-sm md:col-span-6">
                <div className="flex justify-end">
                  <Typography variant="h1" component="span" data-motion-title-1>
                    {title_1}
                  </Typography>
                </div>
                <div className="flex justify-center">
                  <Typography variant="h1" component="span" data-motion-title-2>
                    {title_2}
                  </Typography>
                </div>
                <div className="flex justify-start">
                  <Typography
                    variant="h1"
                    component="span"
                    data-motion-title-3
                    className="underline underline-offset-4"
                  >
                    {title_3}
                  </Typography>
                </div>
              </div>
              <div className="col-span-2 flex flex-col gap-spacing-sm md:col-span-6">
                <div className="flex flex-col gap-spacing-md lg:col-span-5 lg:col-start-7">
                  <div className="flex" data-motion-text>
                    <Typography variant="h6" component="p">
                      {text}
                    </Typography>
                  </div>
                  {link?.url ? (
                    <CtaButton
                      href={link.url}
                      variant="secondary"
                      className="self-start"
                      data-motion-cta
                    >
                      {link?.title || 'Scopri di più'}
                    </CtaButton>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Container>
          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )

  return <>{isMobile ? renderMobile() : renderDesktop()}</>
}

export default CraftedToMatter
